import { orderBy } from 'lodash-es'
import { collection } from '@/infra/collection'
import {
  LegendVersionId,
  PublishedLegend,
  type PublishedLegendProps,
} from './PublishedLegend'
import type { PublishedLegendResource } from '@/features/jobs/api/resources/legend'
import type { PrimeWorkspaceFn } from '@/features/iam'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { type LegendsApi } from '@/features/jobs/api/legend-api'
import { mapPublishedLegendResourceToProps } from './published-legend-mappers'

/**
 * Primes a Published Legend.
 */
export type PrimePublishedLegendFn = (props: LegendVersionId) => PublishedLegend

/**
 * Query for listing published legends.
 */
export interface ListLegendsQuery extends Partial<CursorPageQuery> {
  workspaceId: Id
}

/**
 * Lists published legends.
 */
export type ListLegendsFn = (
  query: ListLegendsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<PublishedLegend>>

/**
 * Store for Published Legends.
 */
export class PublishedLegendStore {
  /**
   * Published legends.
   */
  readonly publishedLegends = collection({
    create: () => new PublishedLegend(),
    // Each version is a different instance.
    getDataId: (props) =>
      LegendVersionId.asString(
        LegendVersionId(props.id || '?', props.version ?? -1),
      ),
    getModelId: (model) => LegendVersionId.asString(model.versionId),
  })

  constructor(
    private readonly _api: LegendsApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {}

  /**
   * Primes a Published Legend.
   *
   * @param versionId
   */
  primePublishedLegend(versionId: LegendVersionId): PublishedLegend {
    return this.publishedLegends.set({
      id: versionId.id,
      version: versionId.version,
    })
  }

  /**
   * Gets the published legend version by ID from the in-memory collection.
   *
   * @param legendVersionId
   */
  get(legendVersionId: LegendVersionId): PublishedLegend {
    return (
      this.publishedLegends.find((l) =>
        LegendVersionId.equals(l.versionId, legendVersionId),
      ) ?? PublishedLegend.none
    )
  }

  /**
   * Returns published legends for the specified Workspace, sorted by View Order.
   * Only includes latest versions.
   *
   * @param workspaceId
   */
  forWorkspace(workspaceId: Id): PublishedLegend[] {
    workspaceId = Id.assert(workspaceId)
    const seen = new Map<string, PublishedLegend>()
    this.publishedLegends.forEach((legend) => {
      if (!Id.some(legend.id)) {
        return
      }

      if (legend.workspace.id !== workspaceId) {
        return
      }

      const existing = seen.get(legend.id)
      if (existing && existing.version > legend.version) {
        return
      }

      seen.set(legend.id, legend)
    })

    return orderBy(
      // NOTE: Need to apply inactive filter after having
      // accumulated the latest versions.
      Array.from(seen.values()).filter((legend) => !legend.inactive),
      [(x) => x.viewOrder, (x) => x.dateCreated],
      ['asc', 'asc'],
    )
  }

  /**
   * Lists the published legends of a workspace.
   *
   * @param query
   * @param signal
   */
  async listLegends(
    query: ListLegendsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<PublishedLegend>> {
    const response = await this._api.publishedLegends.list(
      {
        workspace_id: Id.assert(query.workspaceId),
        after: query.after,
        limit: query.limit,
      },
      signal,
    )
    return {
      next: response.cursor,
      total: response.total,
      data: response.data.map((r) =>
        this.publishedLegends.set(this._mapResourceToProps(r)),
      ),
    }
  }

  /**
   * Fetches a Published Legend by its version ID.
   *
   * @param legendVersionId
   * @param signal
   */
  async fetchLegendVersion(
    legendVersionId: LegendVersionId,
    signal?: AbortSignal,
  ): Promise<PublishedLegend> {
    const resource = await this._api.publishedLegends.getVersion(
      Id.assert(legendVersionId.id),
      legendVersionId.version,
      signal,
    )
    return this.publishedLegends.set(this._mapResourceToProps(resource))
  }

  /**
   * Fetches a Published Legend by its version ID, as served to the engine
   * version this app supports.
   *
   * @param legendVersionId
   * @param signal
   */
  async fetchSupportedLegendVersion(
    legendVersionId: LegendVersionId,
    signal?: AbortSignal,
  ): Promise<PublishedLegend> {
    const resource = await this._api.publishedLegends.getSupportedVersion(
      Id.assert(legendVersionId.id),
      legendVersionId.version,
      signal,
    )
    return this.publishedLegends.set(this._mapResourceToProps(resource))
  }

  /**
   * Fetches the latest version of a Published Legend.
   *
   * @param legendId
   * @param signal
   */
  async fetchLatestLegendVersion(
    legendId: Id,
    signal?: AbortSignal,
  ): Promise<PublishedLegend> {
    const resource = await this._api.publishedLegends.get(
      Id.assert(legendId),
      signal,
    )
    return this.publishedLegends.set(this._mapResourceToProps(resource))
  }

  private _mapResourceToProps(
    resource: PublishedLegendResource,
  ): Partial<PublishedLegendProps> {
    return {
      ...mapPublishedLegendResourceToProps(resource),
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
    }
  }
}
